import { css, cx } from '@linaria/core'
import type { ComponentProps, FC, ReactNode } from 'react'
import { HoverTooltip } from '#components/HoverTooltip'
import { fragments, style } from '#styles'

import { Icon } from './Icon'

const IconButtonClass = css`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  user-select: none;
  color: currentColor;
  ${fragments.textStyle.button.m}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.25s ease;

  &.icon-left {
    flex-direction: row-reverse;
  }

  &._selected,
  &:hover:not(:disabled) {
    color: ${style.themeColors.textImportant};
  }

  &:disabled {
    color: ${style.themeColors.textDisabled};
    cursor: default;
  }

  &.accent {
    color: ${style.themeColors.backgroundAccent};

    &:hover:not(:disabled) {
      color: ${style.themeColors.backgroundAccentHover};
    }
  }

  &.segmented {
    color: ${style.themeColors.textDeselected};

    &:hover:not(:disabled) {
      color: ${style.themeColors.textDefault};
    }

    &.selected {
      color: ${style.themeColors.textImportant};
      background: ${style.themeColors.backgroundBoxed};
    }

    &:hover.selected:not(:disabled) {
      user-select: none;
      cursor: initial;
      color: ${style.themeColors.textImportant};
    }
  }
`

export type IconButtonProps = {
  // language="File Reference"
  iconSize?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  iconUrl: string
  iconClassName?: string
  children?: ReactNode
  title: ReactNode | undefined
  tooltipOffset?: number
  allowTooltipWhenDisabled?: boolean
  forceTooltipOpen?: boolean
  /** pushes a className `_selected` onto the element, good for styling */
  selected?: boolean
  iconFirst?: boolean
} & Omit<ComponentProps<'button'>, 'title'>

export const IconButton: FC<IconButtonProps> = ({
  ref,
  iconUrl,
  className,
  iconClassName,
  iconSize = 's',
  children,
  selected,
  title,
  iconFirst,
  tooltipOffset,
  disabled,
  allowTooltipWhenDisabled,
  forceTooltipOpen,
  ...props
}) => {
  const classNames = cx(IconButtonClass, selected && '_selected', className)

  if (!title) {
    return (
      <button ref={ref} className={classNames} disabled={disabled} {...props}>
        {iconFirst && <Icon className={iconClassName} iconUrl={iconUrl} size={iconSize} />}
        {children}
        {!iconFirst && <Icon className={iconClassName} iconUrl={iconUrl} size={iconSize} />}
      </button>
    )
  }

  return (
    <HoverTooltip.button
      ref={ref}
      title={title}
      className={classNames}
      offset={tooltipOffset}
      allowTooltipWhenDisabled={allowTooltipWhenDisabled}
      forceOpen={forceTooltipOpen}
      type="button"
      disabled={disabled}
      {...props}
    >
      {iconFirst && <Icon className={iconClassName} iconUrl={iconUrl} size={iconSize} />}
      {children}
      {!iconFirst && <Icon className={iconClassName} iconUrl={iconUrl} size={iconSize} />}
    </HoverTooltip.button>
  )
}
